import React, { useState, useMemo, useEffect } from "react";
import { Organization } from "../../../Entities/Organization";
import { Tabs } from "antd";
import {
  ApiOutlined,
  GlobalOutlined,
  AppstoreOutlined,
  KeyOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import Widget from "./integration/Widget";
import API from "./integration/API";
import Web from "./integration/Web";
import APIKeys from "./integration/APIKeys";
import apiKeyService from "../../../Service/api.key.service";
import { useLoader } from "../../../Context/LoaderContext";
import { ApiKey } from "../../../Entities/ApiKey";
import SSO from "./integration/SSO";

interface IntegrationDetailsProps {
  organizationDetails: Organization;
}

const IntegrationDetails: React.FC<IntegrationDetailsProps> = ({
  organizationDetails,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabKey = searchParams.get("subTab") || "widget";
  const [activeSubTabKey, setActiveSubTabKey] = useState<string>(activeTabKey);

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);

  const { setLoading, setLoadingMessage } = useLoader();

  const handleSubTabChange = (key: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("subTab", key);
      return newParams;
    });
    setActiveSubTabKey(key);
  };

  const tabItems = useMemo(
    () => [
      {
        label: "Widget",
        key: "widget",
        children: <Widget key="widget" />,
        icon: <AppstoreOutlined />,
      },
      {
        label: "Web",
        key: "web",
        children: (
          <Web key="web" apiKeys={apiKeys} setSubTab={setActiveSubTabKey} />
        ),
        icon: <GlobalOutlined />,
      },
      {
        label: "API",
        key: "api",
        children: (
          <API key="api" apiKeys={apiKeys} setSubTab={setActiveSubTabKey} />
        ),
        icon: <ApiOutlined />,
      },
      {
        label: "API Keys",
        key: "api-keys",
        children: (
          <APIKeys key="api-keys" apiKeys={apiKeys} setApiKeys={setApiKeys} />
        ),
        icon: <KeyOutlined />,
      },
      {
        label: "SSO",
        key: "sso",
        children: <SSO key="sso" />,
        icon: <LockOutlined />,
      },
    ],
    [apiKeys]
  );

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        setLoadingMessage("Fetching API keys...");
        const apiKeys = await apiKeyService.getAPIKeys();
        setApiKeys(apiKeys);
      } catch (error) {
        console.error("Error fetching org api key:", error);
      } finally {
        setLoading(false);
        setLoadingMessage(null);
      }
    };
    if (organizationDetails) {
      init();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationDetails]);

  return (
    <section
      className="integration-details-container"
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Tabs
        style={{ height: "100%", flex: 1 }}
        tabPosition="left"
        items={tabItems}
        tabBarGutter={16}
        activeKey={activeSubTabKey}
        onChange={handleSubTabChange}
        destroyInactiveTabPane
      />
    </section>
  );
};

export default IntegrationDetails;
